import React, { useState, useEffect, useContext } from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import config from '../../config/config';

import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';

import { HospitalContext } from '../../contexts/HospitalContext';
import { OptionsContext } from '../../contexts/OptionsContext';

import ManageHospitalForm from '../CMSForm/ManageHospitalForm';
import OperatorActionErrorModal from '../CMSForm/OperatorActionErrorModal';

import './ManageFacilities.css';


const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function ManageHospitals() {
  const { hospitals, getHospitals, hospitalCount, getHospitalCount, page, setPage, deleteHospital } = useContext(HospitalContext)


  const [hidden, setHidden] = useState(false)
  const [openMenu, setOpenMenu] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null);
  const [hospitalName, setHospitalName] = useState('')
  const [searchString, setSearchString] = useState('')
  const [filteredData, setFilteredData] = useState([])
  const [editFormData, setEditFormData] = useState({})

  const [errorModal, setErrorModal] = useState(null)
  const [selectedHospitalForDelete, setSelectedHospitalForDelete] = useState(null)

  function toggleForm() {
    setHidden(!hidden)
  }

  useEffect(() => {
    getHospitalCount().catch(() => {})
  }, [])

  useEffect(() => {
    if (page !== undefined) {
      getHospitals(page, searchString).catch(() => {})
    }
  }, [page])

  useEffect(() => {
    setFilteredData([...processData(hospitals)])
  }, [hospitals])

  function processData(data) {
    const newData = data.map(e => createData(e.name, e.formatted_address, e.create_date))
    return newData
  }

  function createData(name, formatted_address, create_date) {
    return { name, formatted_address, create_date };
  }

  function handleSearch(e) {
    let string = e.target.value
    setSearchString(string)
    setPage(1)

    if (string !== '') {
      getHospitals(1, string).catch(() => {})
      getHospitalCount(string).catch(() => {})
    }
    else {
      getHospitals(1).catch(() => {})
      getHospitalCount().catch(() => {})
    }
  }

  function handleCreateNew() {
    setEditFormData({})
    toggleForm()
  }

  function handleEditClick(i) {
    let selected = getCurrListData(i)
    setEditFormData({ ...selected })
    toggleForm()
  }

  function getCurrListData(i) {
    return hospitals[i]
  }

  async function handleDelete(selected) {
    try {
      await deleteHospital(selected._id)
      setErrorModal(null)
      setSelectedHospitalForDelete(null)
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data
        if (errorData.error === 'organization_has_active_subscriptions' ||
            errorData.error === 'organization_has_assigned_users') {
          setErrorModal(errorData)
          return
        }
      }
      setSelectedHospitalForDelete(null)
      throw error
    }
  }

  function handleDeleteClick(i) {
    let selected = getCurrListData(i)
    const confirm = window.confirm('Are you sure you want to delete this hospital?')
    if (confirm) {
      setSelectedHospitalForDelete(selected)
      handleDelete(selected)
    }
  }

  const handleRetry = () => {
    if (selectedHospitalForDelete) handleDelete(selectedHospitalForDelete)
  }

  const handleCancelErrorModal = () => {
    setErrorModal(null)
    setSelectedHospitalForDelete(null)
  }

  function handleOpenMenu(e, name) {
    setOpenMenu(true)
    setAnchorEl(e.currentTarget)
    setHospitalName(name)
  }

  function handleCloseMenu() {
    setOpenMenu(false)
    setAnchorEl(null)
  }

  function handleCopyLink(type) {
    let link = ''
    const name = hospitalName.toLowerCase().replace(/ /g, '-')
    if (type === 'login') {
      link = `${config.appUrl}/hospital/login?hospital=${name}`
    } else if (type === 'dashboard') {
      link = `${config.appUrl}/hospital/listings/${name}`
    }
    navigator.clipboard.writeText(link)
  }

  return (
    <div>
      <OperatorActionErrorModal
        error={errorModal}
        onRetry={handleRetry}
        onCancel={handleCancelErrorModal}
        onRetryWithCancelSubscription={handleCancelErrorModal}
        retryWithCancelLabel="Dismiss"
      />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar style={{ backgroundColor: 'white' }} position="static">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }} >
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                inputProps={{ 'aria-label': 'search' }}
                onChange={(e) => { handleSearch(e) }}
                value={searchString}
              />
            </Search>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button onClick={handleCreateNew} variant="contained">Create New</Button>
            </div>
          </Toolbar>
        </AppBar>
      </Box>

      {/* form */}
      {hidden &&
        <ManageHospitalForm
          initialData={editFormData}
          closeForm={() => { toggleForm() }}
        />
      }


      <TableContainer style={{ marginTop: '30px' }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Create Date</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, i) => (
              <TableRow
                key={row.name + i}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.formatted_address}</TableCell>
                {/* <TableCell>{row.published ? 'Published' : 'Unpublished'}</TableCell> */}
                <TableCell>{new Date(row.create_date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>

                    <TableOption
                      color={'primary'}
                      name={'Edit'}
                      onClick={() => { handleEditClick(i) }}
                    >
                      <EditIcon />
                    </TableOption>

                    <TableOption
                      color={'primary'}
                      name={'Delete'}
                      onClick={() => { handleDeleteClick(i) }}
                    >
                      <DeleteIcon />
                    </TableOption>

                    <div style={{ position: 'relative' }}>
                      <IconButton
                        id="basic-button"
                        ref={anchorEl}
                        onClick={(e) => { handleOpenMenu(e, row.name) }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </div>

                  </div>
                </TableCell>
              </TableRow>
            ))}
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleCloseMenu}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem onClick={() => { handleCopyLink('login') }}>copy login</MenuItem>
              <MenuItem onClick={() => { handleCopyLink('dashboard') }}>copy dashboard</MenuItem>
            </Menu>
          </TableBody>
        </Table>
      </TableContainer>

      {hospitalCount > 15 &&
        <div style={{ width: '100%', margin: '40px 0px', display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2}>
            <Pagination
              page={page}
              onChange={(e, i) => { setPage(i) }}
              count={Math.ceil(hospitalCount / 15)}
              showFirstButton={hospitalCount / 15 > 5 ? true : false}
              showLastButton={hospitalCount / 15 > 5 ? true : false}
            />
          </Stack>
        </div>
      }

    </div>
  )
}

export default ManageHospitals

const TableOption = ({ children, name, color, onClick }) => {
  return (
    <div style={{ margin: '0px 10px', cursor: 'pointer' }} onClick={onClick}>
      <Tooltip title={name}>
        <IconButton color={color}>
          {children}
        </IconButton>
      </Tooltip>
    </div>
  )
}